/**
 * Stage 4 — Say the Letter.
 *
 * Target: child pronounces the current letter's sound (e.g. "بَ"). Each
 * attempt flows:
 *   1. Play the expected sound so the child hears the model.
 *   2. Child press-and-holds the mic, speaks, releases.
 *   3. Recorder → POST /api/ai/evaluate (Whisper + Llama).
 *   4. Render feedback bubble with the Arabic + English tip.
 *
 * 3 correct pronunciations pass the stage. Stars:
 *   - 3 stars if achieved in exactly 3 attempts.
 *   - 2 stars in ≤ 5.
 *   - 1 star in ≤ 8.
 *   - 0 stars otherwise (but the child can keep trying).
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api, ApiError } from '../lib/api';
import { playUrl, unlockAudio, letterNameUrl } from '../lib/audio';
import { MicButton } from '../components/MicButton';
import { Spinner } from '../components/Spinner';
import { Celebration } from '../components/Celebration';
import { NavButton } from '../components/NavButton';
import { currentUser } from '../state/user';
import { refreshMap } from '../state/content';
import type { LetterSummary } from '../state/content';
import type { EvaluateResponse } from '@huroof/shared';
import type { RecordResult } from '../lib/recorder';

const TARGET_CORRECT = 3;

interface Attempt extends EvaluateResponse {
  transcript: string;
}

export function StageSay() {
  const { route } = useLocation();
  const { params } = useRoute();
  const letterId = Number(params.id);

  const [letter, setLetter] = useState<LetterSummary | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [lastResult, setLastResult] = useState<Attempt | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [finalStars, setFinalStars] = useState<number | null>(null);

  useEffect(() => {
    api<{ ok: true; letter: LetterSummary }>(`/api/letters/${letterId}`).then(
      (r) => {
        setLetter(r.letter);
        unlockAudio();
      },
    );
  }, [letterId]);

  async function submit(r: RecordResult) {
    if (!letter) return;
    if (r.durationMs < 250) {
      setErr('اِسْتَمِرَّ فِي الضَّغْطِ قَلِيلًا أَكْثَرَ');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      // Send raw bytes (Blob) as the request body. The Worker reads
      // c.req.arrayBuffer() and hands them to Whisper.
      const bytes = await r.blob.arrayBuffer();
      // HTTP headers are ASCII-only — the expected text contains Arabic
      // with tashkeel, so pass it as a URL-encoded query param instead
      // of a header. Body stays as raw audio bytes.
      const expected = encodeURIComponent(letter.name_ar);
      const res = await fetch(`/api/ai/evaluate?expected=${expected}`, {
        method: 'POST',
        headers: {
          'X-Device-Id': localStorage.getItem('huroof:device-id') ?? '',
          'Content-Type': r.mimeType,
        },
        body: bytes,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { kid_message_ar?: string; kid_message_en?: string }
          | null;
        throw new ApiError({
          ok: false,
          code: res.status,
          kid_message_ar: body?.kid_message_ar ?? 'حَاوِلْ مَرَّةً أُخْرَى!',
          kid_message_en: body?.kid_message_en ?? 'Try again!',
        });
      }
      const result = (await res.json()) as Attempt & { ok: true };
      setLastResult(result);
      setAttempts((a) => [...a, result]);

      try {
        navigator.vibrate?.(result.correct ? 25 : [15, 30, 15]);
      } catch {
        /* unsupported */
      }

      // If they passed, finish up.
      const nowCorrect = attempts.filter((a) => a.correct).length + (result.correct ? 1 : 0);
      if (nowCorrect >= TARGET_CORRECT) {
        setTimeout(() => finish(nowCorrect, attempts.length + 1), 1200);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e.kidMessageAr);
      } else {
        setErr(String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function finish(correct: number, totalAttempts: number) {
    const stars =
      correct >= TARGET_CORRECT && totalAttempts <= 3
        ? 3
        : correct >= TARGET_CORRECT && totalAttempts <= 5
          ? 2
          : correct >= TARGET_CORRECT && totalAttempts <= 8
            ? 1
            : 0;

    if (currentUser.value) {
      try {
        await api('/api/progress', {
          method: 'POST',
          body: {
            letterId,
            stage: 4,
            stars,
            attempts: totalAttempts,
            errors: attempts
              .filter((a) => !a.correct)
              .map((a) => ({
                expected: letter?.letter ?? '',
                actual: a.transcript,
              })),
          },
        });
        void refreshMap();
      } catch {
        // non-fatal
      }
    }
    setFinalStars(stars);
    setTimeout(() => route(`/island/${letterId}`), 1600);
  }

  const correctCount = attempts.filter((a) => a.correct).length;

  if (!letter) {
    return (
      <div class="flex-1 grid place-items-center">
        <Spinner />
      </div>
    );
  }

  if (finalStars !== null) {
    return (
      <Celebration
        stars={finalStars}
        scoreText={`${Math.min(correctCount, TARGET_CORRECT)}/${TARGET_CORRECT}`}
      />
    );
  }

  return (
    <div class="flex-1 flex flex-col items-center justify-between px-4 py-6 pb-10 max-w-md mx-auto w-full">
      <header class="w-full flex items-center justify-between">
        <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={() => route(`/island/${letterId}`)} />
        <div class="flex gap-1">
          {Array.from({ length: TARGET_CORRECT }).map((_, i) => (
            <span
              key={i}
              class={`h-3 w-3 rounded-full ${i < correctCount ? 'bg-leaf' : 'bg-ink/15'}`}
            />
          ))}
        </div>
      </header>

      <div class="flex-1 flex flex-col items-center justify-center gap-6 w-full">
        <p class="text-sm font-display text-ink/60">قُلِ اِسْمَ الحَرْفِ</p>
        <p class="text-[10px] text-ink/40 -mt-2" dir="ltr">
          Say the letter's name
        </p>
        <p
          class="font-display text-coral select-none"
          style={{
            fontSize: 'clamp(100px, 34vw, 220px)',
            lineHeight: '1.15',
            paddingTop: '0.08em',
          }}
        >
          {letter.letter}
        </p>
        <button
          type="button"
          onClick={() => void playUrl(letterNameUrl(letter.id))}
          class="min-h-[64px] px-6 py-3 rounded-2xl bg-ocean text-white shadow-lg shadow-ocean/30 active:scale-95 flex flex-col items-center"
        >
          <span class="font-display">🔊 اِسْتَمِعْ أَوَّلًا</span>
          <span class="text-[10px] opacity-80" dir="ltr">Listen first</span>
        </button>

        {busy && !lastResult && (
          <Spinner label="جَارٍ التَّحْقُّقُ…" labelEn="Checking…" />
        )}

        {lastResult && !busy && (
          <div
            class={`w-full rounded-2xl p-4 text-center ${
              lastResult.correct
                ? 'bg-leaf/15 border-2 border-leaf'
                : 'bg-berry/10 border-2 border-berry/50'
            }`}
          >
            <p class="text-xl font-display animate-pop">
              {lastResult.correct ? '✅ أَحْسَنْتَ!' : '🌱 تَقْرِيبًا!'}
            </p>
            {lastResult.feedback_ar && (
              <p class="text-base mt-2 font-display">{lastResult.feedback_ar}</p>
            )}
            {lastResult.feedback_en && (
              <p class="text-xs mt-2 text-ink/60" dir="ltr">
                {lastResult.feedback_en}
              </p>
            )}
            <p class="text-xs mt-2 text-ink/40" dir="ltr">
              we heard: "{lastResult.transcript}"
            </p>
          </div>
        )}

        {err && <p class="text-sm text-berry">{err}</p>}
      </div>

      <div class="flex flex-col items-center gap-3">
        <MicButton
          onResult={submit}
          onError={(e) => {
            console.warn('[StageSay] recorder error:', e);
            const reason =
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e as any)?.reason ?? (e instanceof Error ? e.name : '');
            if (reason === 'permission_denied' || reason === 'NotAllowedError') {
              setErr(
                'يَرْجَى السَّمَاحُ بِاسْتِخْدَامِ المِيكْرُوفُون — please allow microphone access in your browser settings, then reload.',
              );
            } else if (reason === 'no_mic_found' || reason === 'NotFoundError') {
              setErr('لَا يُوجَدُ مِيكْرُوفُون — no microphone found on this device.');
            } else if (reason === 'unsupported') {
              setErr(
                'المُتَصَفِّحُ لَا يَدْعَمُ التَّسْجِيلَ — this browser cannot record audio.',
              );
            } else if (reason === 'encoding_failed') {
              setErr(
                'لَمْ نَسْتَطِعْ تَسْجِيلَ الصَّوْتِ — recording failed. Try again.',
              );
            } else {
              setErr('لَمْ نَسْمَعْكَ. حَاوِلْ مَرَّةً أُخْرَى — try again.');
            }
          }}
          disabled={busy}
          label="Press and hold to speak"
        />
        <p class="text-xs text-ink/50" dir="ltr">
          Press and hold, speak, release
        </p>
      </div>
    </div>
  );
}
